import { Box, Button, TextField } from '@mui/material';
import { MAX_MANUAL_VESSEL_LIMIT } from '../../../../models/my-fleet.model';

interface MyFleetAddVesselsManualInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (imos: string[]) => void;
}

function isValidIMO(imo: string) {
  return /^\d{7}$/.test(imo);
}

function validateIMOs(input: string): {
  valid: boolean;
  invalidIMOs: string[];
  duplicateIMOs: string[];
  imos: string[];
} {
  if (!input.trim())
    return { valid: true, invalidIMOs: [], duplicateIMOs: [], imos: [] };
  const imos = input.split(',').map((imo) => imo.trim());
  const invalidIMOs = imos.filter((imo) => !isValidIMO(imo));
  const seen = new Set<string>();
  const duplicateIMOs = imos.filter((imo) => {
    if (!isValidIMO(imo)) return false;
    if (seen.has(imo)) return true;
    seen.add(imo);
    return false;
  });
  const valid = invalidIMOs.length === 0 && duplicateIMOs.length === 0;
  return { valid, invalidIMOs, duplicateIMOs, imos };
}

function MyFleetAddVesselsManualInput({
  value,
  onChange,
  onSubmit,
}: MyFleetAddVesselsManualInputProps) {
  const { valid, invalidIMOs, duplicateIMOs, imos } = validateIMOs(value);
  const exceedsLimit = imos.length > MAX_MANUAL_VESSEL_LIMIT;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (valid && imos.length > 0 && !exceedsLimit) {
      onSubmit(imos);
    }
  };

  let helperText = `*Enter IMO in the format "1234567,2345678" if adding multiple vessels,
             please separate by a comma.`;
  if (exceedsLimit) {
    helperText = `You can only add up to ${MAX_MANUAL_VESSEL_LIMIT} vessels at once. Please reduce your selection.`;
  } else if (!valid) {
    const errors: string[] = [];
    if (invalidIMOs.length > 0) {
      errors.push(`Invalid IMO(s): ${invalidIMOs.join(', ')}`);
    }
    if (duplicateIMOs.length > 0) {
      errors.push(`Duplicate IMO(s): ${duplicateIMOs.join(', ')}`);
    }
    helperText = errors.join(' | ');
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={!valid || exceedsLimit}
        helperText={helperText}
        label="Vessel IMO Input"
        inputProps={{
          'aria-label': 'Vessel IMO Input',
          'data-testid': 'vessel-imo-input',
        }}
        required
        fullWidth
        margin="dense"
        placeholder="1234567, 2345678"
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={!valid || !value.trim() || exceedsLimit}
        aria-label="create vessels button"
        data-testid="create-vessels-button"
      >
        Create Vessel(s)
      </Button>
    </Box>
  );
}

export default MyFleetAddVesselsManualInput;
